import { useState, useMemo } from 'react';
import { formatCurrency, CATEGORY_MAP } from '@tally/shared';
import { ArrowUpDown, ArrowUp, ArrowDown, ExternalLink, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';
import { ROUTES } from '@/routes';
import { TransactionDetailPanel } from './TransactionDetailPanel';

type SortField = 'date' | 'amount' | 'merchantName' | 'category';
type SortOrder = 'asc' | 'desc';

export interface TransactionRow {
  id: string;
  date: string;
  merchantName: string;
  amount: number;
  currency?: string;
  amountUsd?: number | null;
  chargeType?: 'charge' | 'refund' | 'estimated';
  category: string;
  tripId?: string | null;
  tripName?: string | null;
  description?: string | null;
  location?: { city?: string | null; country?: string | null } | null;
  sources?: Array<{ source: string; deepLink: string | null; accountName?: string }>;
  metadata?: Record<string, unknown>;
}

interface TransactionTableProps {
  transactions: TransactionRow[];
  showTripColumn?: boolean;
  showSearch?: boolean;
  pageSize?: number;
  compact?: boolean;
}

export function TransactionTable({
  transactions,
  showTripColumn = true,
  showSearch = true,
  pageSize = 30,
  compact = false,
}: TransactionTableProps) {
  const [sortBy, setSortBy] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTx, setSelectedTx] = useState<TransactionRow | null>(null);

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const filtered = useMemo(() => {
    let result = [...transactions];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) => t.merchantName.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)
      );
    }

    if (categoryFilter) {
      result = result.filter((t) => t.category === categoryFilter);
    }

    result.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'amount': cmp = a.amount - b.amount; break;
        case 'merchantName': cmp = a.merchantName.localeCompare(b.merchantName); break;
        case 'category': cmp = a.category.localeCompare(b.category); break;
        default: cmp = a.date.localeCompare(b.date); break;
      }
      return sortOrder === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [transactions, searchQuery, categoryFilter, sortBy, sortOrder]);

  const total = filtered.length;
  const totalPages = Math.ceil(total / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortOrder === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />;
  };

  const categories = useMemo(() => {
    const set = new Set(transactions.map((t) => t.category));
    return [...set].sort();
  }, [transactions]);

  return (
    <div className="space-y-3">
      {showSearch && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Filter transactions..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              className="w-full rounded-lg border border-input bg-background py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm"
          >
            <option value="">All categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{CATEGORY_MAP.get(cat)?.label || cat}</option>
            ))}
          </select>
          {(searchQuery || categoryFilter) && (
            <button
              onClick={() => { setSearchQuery(''); setCategoryFilter(''); setPage(1); }}
              className="rounded-lg px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-accent"
            >
              Clear
            </button>
          )}
          <span className="text-xs text-muted-foreground ml-auto">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50">
            <tr className="text-left text-xs text-muted-foreground">
              <th className={cn('px-4 font-medium', compact ? 'py-2' : 'py-3')}>
                <button onClick={() => handleSort('date')} className="flex items-center gap-1.5 hover:text-foreground">
                  Date <SortIcon field="date" />
                </button>
              </th>
              <th className={cn('px-4 font-medium', compact ? 'py-2' : 'py-3')}>
                <button onClick={() => handleSort('merchantName')} className="flex items-center gap-1.5 hover:text-foreground">
                  Merchant <SortIcon field="merchantName" />
                </button>
              </th>
              <th className={cn('px-4 font-medium', compact ? 'py-2' : 'py-3')}>
                <button onClick={() => handleSort('category')} className="flex items-center gap-1.5 hover:text-foreground">
                  Category <SortIcon field="category" />
                </button>
              </th>
              {showTripColumn && (
                <th className={cn('px-4 font-medium', compact ? 'py-2' : 'py-3')}>Trip</th>
              )}
              <th className={cn('px-4 font-medium', compact ? 'py-2' : 'py-3')}>Account / Source</th>
              <th className={cn('px-4 font-medium', compact ? 'py-2' : 'py-3')}>
                <button onClick={() => handleSort('amount')} className="flex items-center gap-1.5 hover:text-foreground ml-auto">
                  Amount <SortIcon field="amount" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paged.length === 0 ? (
              <tr>
                <td colSpan={showTripColumn ? 6 : 5} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {searchQuery || categoryFilter ? 'No transactions match your filters' : 'No transactions'}
                </td>
              </tr>
            ) : (
              paged.map((tx) => (
                <tr key={tx.id} onClick={() => setSelectedTx(tx)} className="transition-colors hover:bg-accent/30 cursor-pointer">
                  <td className={cn('px-4 text-muted-foreground whitespace-nowrap', compact ? 'py-2' : 'py-3')}>
                    {tx.date?.split('T')[0]}
                  </td>
                  <td className={cn('px-4', compact ? 'py-2' : 'py-3')}>
                    <p className="font-medium">{tx.merchantName}</p>
                    {!compact && tx.description && tx.description !== tx.merchantName && (
                      <p className="text-xs text-muted-foreground truncate max-w-[200px]">{tx.description}</p>
                    )}
                  </td>
                  <td className={cn('px-4', compact ? 'py-2' : 'py-3')}>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2 py-0.5 text-xs">
                      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: CATEGORY_MAP.get(tx.category)?.color }} />
                      {CATEGORY_MAP.get(tx.category)?.label || tx.category}
                    </span>
                  </td>
                  {showTripColumn && (
                    <td className={cn('px-4', compact ? 'py-2' : 'py-3')}>
                      {tx.tripId ? (
                        <a href={ROUTES.app.trip(tx.tripId)} className="block max-w-[180px] truncate text-xs text-primary hover:underline">{tx.tripName || 'View trip'}</a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  )}
                  <td className={cn('px-4', compact ? 'py-2' : 'py-3')}>
                    <div className="flex flex-wrap gap-1.5">
                      {tx.sources?.map((s, i) => (
                        s.deepLink ? (
                          <a key={i} href={s.deepLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary hover:bg-primary/20">
                            {s.accountName || s.source} <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        ) : (
                          <span key={i} className="rounded bg-muted px-1.5 py-0.5 text-xs">{s.accountName || s.source}</span>
                        )
                      ))}
                    </div>
                  </td>
                  <td className={cn('px-4 text-right font-semibold whitespace-nowrap', compact ? 'py-2' : 'py-3')}>
                    <div className={cn(tx.chargeType === 'refund' && 'text-green-600 dark:text-green-400')}>
                      {formatCurrency(tx.amount, tx.currency)}
                    </div>
                    {tx.currency && tx.currency !== 'USD' && tx.amountUsd != null && (
                      <div className="text-xs font-normal text-muted-foreground">≈ {formatCurrency(tx.amountUsd, 'USD')}</div>
                    )}
                    {tx.chargeType === 'refund' && <div className="text-xs font-normal text-green-600 dark:text-green-400">refund</div>}
                    {tx.chargeType === 'estimated' && <div className="text-xs font-normal text-amber-600 dark:text-amber-400">estimated</div>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <p className="text-xs text-muted-foreground">
            {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg p-1.5 hover:bg-accent disabled:opacity-30">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs font-medium">Page {page} of {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="rounded-lg p-1.5 hover:bg-accent disabled:opacity-30">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {selectedTx && (
        <TransactionDetailPanel transaction={selectedTx} onClose={() => setSelectedTx(null)} />
      )}
    </div>
  );
}
